import type { ReactNode } from "react";
import { cn } from "./cn";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Spinner } from "./Spinner";

export const TABLE_ALIGNS = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const satisfies Record<string, string>;

export type Align = keyof typeof TABLE_ALIGNS;

const TABLE_DENSITY_CLASSES = {
  comfortable: "px-4 py-3",
  compact: "px-3 py-2",
} as const satisfies Record<string, string>;

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  field?: keyof T;
  align?: Align;
  width?: string;
  className?: string;
  hideOnMobile?: boolean;
  render?: (row: T) => ReactNode;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  emptyText?: ReactNode;
  onRowClick?: (row: T) => void;
  className?: string;
  compact?: boolean;
  mobileCards?: boolean;
}

function cellContent<T>(column: TableColumn<T>, row: T): ReactNode {
  if (column.render) return column.render(row);
  if (column.field === undefined) return null;
  const value = row[column.field];
  return value === null || value === undefined ? "—" : String(value);
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyText = "Sin resultados",
  onRowClick,
  className,
  compact = false,
  mobileCards = true,
}: TableProps<T>) {
  const isMobile = useIsMobile();
  const cellPad = TABLE_DENSITY_CLASSES[compact ? "compact" : "comfortable"];

  if (mobileCards && isMobile) {
    const cardColumns = columns.filter((column) => !column.hideOnMobile);
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {loading ? (
          <div className="rounded-2xl border border-line bg-card px-4 py-10 text-center text-content-muted">
            <Spinner size="md" className="text-primary-500" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-line bg-card px-4 py-10 text-center text-xs text-content-muted">
            {emptyText}
          </div>
        ) : (
          rows.map((row, index) => (
            <div
              key={rowKey(row, index)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "flex flex-col gap-2 rounded-2xl border border-line bg-card p-3 text-xs",
                onRowClick ? "cursor-pointer active:bg-subtle" : undefined,
              )}
            >
              {cardColumns.map((column) => (
                <div key={column.key} className="flex items-start justify-between gap-3">
                  {column.header ? (
                    <span className="shrink-0 font-semibold uppercase tracking-wide text-content-muted">
                      {column.header}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 break-words text-right text-content">
                    {cellContent(column, row)}
                  </span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-line bg-card",
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-line bg-surface">
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    cellPad,
                    "text-xs font-semibold uppercase tracking-wide text-content-muted",
                    TABLE_ALIGNS[column.align ?? "left"],
                    column.className,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-content-muted">
                  <Spinner size="md" className="text-primary-500" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-content-muted">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={rowKey(row, index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-line last:border-0 transition-colors",
                    onRowClick ? "cursor-pointer hover:bg-subtle" : undefined,
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        cellPad,
                        "text-content",
                        TABLE_ALIGNS[column.align ?? "left"],
                        column.className,
                      )}
                    >
                      {cellContent(column, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
